package ai.wagemix.logic.share

import ai.wagemix.logic.WMBaseDataGateway
import ai.wagemix.logic.isForUser
import ai.wagemix.logic.session
import ai.wagemix.net.models.WMOwnedShare
import ai.wagemix.net.models.WMShareStats
import ai.wagemix.net.models.WMSharedMixPreviewStatus
import ai.wagemix.net.models.WM_SHARED_MIX_PREVIEW_STALE_AFTER
import ai.wagemix.net.models.canRegeneratePreview
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlin.time.Duration
import kotlin.time.Instant

/** What the share dialog and the shared links page show for a shared link's preview image. */
enum class WMSharePreviewState {
    /** The API is rendering it (or the record has not arrived yet). */
    Generating,
    Ready,
    /** It failed, or stalled, or was never tracked: the owner may regenerate it. */
    Failed,
}

/** The preview state of this share at [now]; before its record arrives it is [WMSharePreviewState.Generating]. */
fun WMOwnedShare?.previewState(now: Instant): WMSharePreviewState {
    return when {
        this == null -> WMSharePreviewState.Generating
        canRegeneratePreview(now) -> WMSharePreviewState.Failed
        previewImageUrl != null -> WMSharePreviewState.Ready
        else -> WMSharePreviewState.Generating
    }
}

/** How long until a pending preview counts as stalled, or `null` when it is not pending or has stalled already. */
internal fun WMOwnedShare.previewStalesIn(now: Instant): Duration? {
    val requested = previewRequestedAt ?: return null
    if (previewStatus != WMSharedMixPreviewStatus.Pending) {
        return null
    }
    val remaining = requested + WM_SHARED_MIX_PREVIEW_STALE_AFTER - now
    return remaining.takeIf { it.isPositive() }
}

/** The user's mirrored shares as they change, starting with the current list; `null` until the mirror arrives. */
internal fun WMBaseDataGateway.ownSharesFlow(uid: String?): Flow<List<WMOwnedShare>?> {
    return contextUpdateFlow
        .filter { it.isForUser(uid) && it.ownSharesChanged() }
        .map { update -> update.after.ownShares() }
        .onStart { emit(retrieveUserContext(uid).ownShares()) }
}

/** The click statistics of the user's share links as they change; `null` on any plan but Pro. */
internal fun WMBaseDataGateway.shareStatsFlow(uid: String?): Flow<List<WMShareStats>?> {
    return contextUpdateFlow
        .filter { it.isForUser(uid) && it.shareStatsChanged() }
        .map { update -> update.after.shareStats() }
        .onStart { emit(retrieveUserContext(uid).shareStats()) }
}

/** The signed-in user's record of [sharedMixId] as it changes, starting with the current one. */
@OptIn(ExperimentalCoroutinesApi::class)
internal fun WMBaseDataGateway.ownShareFlow(sharedMixId: String): Flow<WMOwnedShare?> {
    return flow { emit(session()?.uid) }
        .flatMapLatest { uid -> ownSharesFlow(uid) }
        .map { shares -> shares?.firstOrNull { it.sharedMixId == sharedMixId } }
}

/**
 * The preview state of each share as it changes. A pending preview is looked at again when it would stall, so a
 * render that never reports back turns into [WMSharePreviewState.Failed] without any new data.
 */
@OptIn(ExperimentalCoroutinesApi::class)
internal fun Flow<WMOwnedShare?>.previewStates(now: () -> Instant): Flow<WMSharePreviewState> {
    return flatMapLatest { share ->
        flow {
            emit(share.previewState(now()))
            val stalesIn = share?.previewStalesIn(now())
            if (stalesIn != null) {
                delay(stalesIn)
                emit(share.previewState(now()))
            }
        }
    }.distinctUntilChanged()
}
